import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Check, X } from "lucide-react";
import { api, arabicError } from "../lib/api";

export const leaveWhen = (lv) => lv.kind === "daily" ? `${lv.from_date} ← ${lv.to_date}` : `${lv.date} · ${lv.from_time} – ${lv.to_time}`;

export const StatusBadge = ({ lv }) => {
  const cls = lv.status === "approved" ? "bg-green-100 text-green-700" : lv.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";
  return <span className={`text-xs font-bold px-2 py-1 rounded whitespace-nowrap ${cls}`} data-testid={`leave-status-${lv.id}`}>{lv.status_ar}</span>;
};

export const LeaveCard = ({ lv, actions, showEmployee }) => (
  <div className="p-4 border-b border-[#EBF4F6] last:border-0 flex flex-wrap items-start gap-3" data-testid={`leave-row-${lv.id}`}>
    <div className="flex-1 min-w-[180px] space-y-1">
      {showEmployee && <div className="font-bold text-[#333333]" data-testid={`leave-employee-${lv.id}`}>{lv.employee_name}</div>}
      <div className="text-sm text-[#09637E] font-bold">{lv.kind_ar}</div>
      <div className="text-sm text-[#333333]" dir="ltr" style={{ textAlign: "right" }} data-testid={`leave-when-${lv.id}`}>{leaveWhen(lv)}</div>
      {lv.note && <div className="text-xs text-[#666666]">السبب: {lv.note}</div>}
      {lv.decision_note && <div className="text-xs text-[#666666]">ملاحظة صاحب العمل: {lv.decision_note}</div>}
      <div className="text-[11px] text-[#999]">قُدّم: {new Date(lv.created_at).toLocaleString("ar-SY")}{lv.decided_at && ` · القرار: ${new Date(lv.decided_at).toLocaleString("ar-SY")}`}</div>
    </div>
    <StatusBadge lv={lv} />
    {actions}
  </div>
);

const EmployeeLeavesPanel = () => {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/employees/leaves", { params: tab === "pending" ? { status: "pending" } : {} })
      .then((r) => setItems(tab === "pending" ? r.data : r.data.filter((x) => x.status !== "pending")))
      .catch((e) => toast.error(arabicError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (lv, action) => {
    const note = action === "reject" ? (window.prompt("سبب الرفض (اختياري):") ?? null) : null;
    if (action === "reject" && note === null) return;
    try {
      const r = await api.patch(`/employees/leaves/${lv.id}`, { action, note: note || undefined });
      toast.success(`تم: ${r.data.status_ar}`); load();
    } catch (e) { toast.error(arabicError(e)); }
  };

  return (
    <section className="space-y-3" data-testid="merchant-leaves-panel">
      <h2 className="text-xl font-extrabold text-[#333333] flex items-center gap-2"><CalendarDays className="text-[#09637E]" /> طلبات الإجازات</h2>
      <div className="grid grid-cols-2 gap-2 bg-[#EBF4F6] p-1 rounded-lg">
        <button onClick={() => setTab("pending")} data-testid="leaves-tab-pending" className={`h-10 rounded-md font-bold text-sm ${tab === "pending" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}>قيد الانتظار</button>
        <button onClick={() => setTab("history")} data-testid="leaves-tab-history" className={`h-10 rounded-md font-bold text-sm ${tab === "history" ? "bg-[#09637E] text-white" : "text-[#333333]"}`}>السجل</button>
      </div>
      <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid="merchant-leaves-list">
        {loading ? <div className="p-6 text-center text-[#666666]">جارٍ التحميل...</div> : items.length === 0 ? (
          <div className="p-8 text-center text-[#666666]" data-testid="merchant-leaves-empty">{tab === "pending" ? "لا توجد طلبات قيد الانتظار" : "لا يوجد سجل إجازات بعد"}</div>
        ) : items.map((lv) => (
          <LeaveCard key={lv.id} lv={lv} showEmployee actions={lv.status === "pending" && (
            <div className="flex gap-2">
              <button onClick={() => decide(lv, "approve")} data-testid={`leave-approve-${lv.id}`} className="h-10 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1"><Check size={16} /> قبول</button>
              <button onClick={() => decide(lv, "reject")} data-testid={`leave-reject-${lv.id}`} className="h-10 px-3 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center gap-1"><X size={16} /> رفض</button>
            </div>
          )} />
        ))}
      </div>
    </section>
  );
};

export default EmployeeLeavesPanel;
